import { TRUE_LOWERCASE, Y } from '@/scripts/constants/booleans'
import {
  FLUID_PCP,
  GUEST_ORDER_ITEM_DETAILS_VIEW,
  PDP_DISPLAY,
  PDP_DISPLAY_VIEW,
  RB_CATEGORY_DISPLAY_VIEW,
  SHOPCART_DISPLAY,
  SHOPCART_DISPLAY_VIEW,
  SHOP_CART,
  COREMEDIA_CONTENT,
  STATIC_PAGE_VIEW,
  VIEW_ORDERS,
  MY_ORDERS_VIEW,
  RAYBAN_SEARCH_DISPLAY_VIEW,
  ORDER_OK_VIEW,
  DELIVERY_DETAILS_VIEW,
  PAYMENT_DETAILS_VIEW,
  PAYMENT_REDESIGN_DETAILS_VIEW,
  DELIVERY_DETAILS_REDESIGN_VIEW,
  THANK_YOU_PAGE_VIEW,
} from '@/scripts/constants/pages'
import { WcsStoreId } from './types/store-id'
import { WcsConfig, WcsEndpoints } from './types/wcs-config'
import { CURRENCY_PLACEHOLDER, PRICE_PLACEHOLDER } from '@/scripts/constants/placeholders'
export default class WcsConfigSingleton {
  private static instance: WcsConfigSingleton | null
  private wcsConfig: WcsConfig
  private wcsEndpoints: WcsEndpoints
  private constructor(wcsConfig: any, wcsEndpoints: WcsEndpoints) {
    this.wcsConfig = this.normalizeWcsConfig(wcsConfig)
    this.wcsEndpoints = wcsEndpoints
  }
  public static getInstance(): WcsConfigSingleton {
    if (!WcsConfigSingleton.instance) {
      const wcsConfig = window.wcs_config ?? {}
      const wcsEndpoints = window.wcs_endpoints ?? {}
      WcsConfigSingleton.instance = new WcsConfigSingleton(wcsConfig, wcsEndpoints)
    }
    return WcsConfigSingleton.instance
  }

  public static resetInstance() {
    this.instance = null
  }
  public static getStoreConfig() {
    const singleton = WcsConfigSingleton.getInstance()
    return singleton.wcsConfig
  }

  public static getEndPoint(key: string): string {
    const singleton = WcsConfigSingleton.getInstance()
    return singleton.wcsEndpoints[key]
  }

  public static readonly isInCart = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return (
      viewTaskName === SHOPCART_DISPLAY_VIEW ||
      viewTaskName === SHOPCART_DISPLAY ||
      viewTaskName === SHOP_CART
    )
  }

  public static readonly isInDelivery = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === DELIVERY_DETAILS_VIEW || viewTaskName === DELIVERY_DETAILS_REDESIGN_VIEW
  }

  public static readonly isInPdp = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === PDP_DISPLAY_VIEW || viewTaskName === PDP_DISPLAY
  }

  public static readonly isInPlp = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === RB_CATEGORY_DISPLAY_VIEW
  }

  public static readonly isInSearch = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === RAYBAN_SEARCH_DISPLAY_VIEW
  }

  public static readonly isInFluidPcp = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === FLUID_PCP
  }

  public static readonly isInOrderDetailsPage = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === VIEW_ORDERS || viewTaskName === GUEST_ORDER_ITEM_DETAILS_VIEW
  }

  public static readonly isInOrdersHistoryPage = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === MY_ORDERS_VIEW
  }

  public static readonly isUSA = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.USA === storeId
  }

  public static readonly isCanada = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.CANADA === storeId
  }

  public static readonly isSpain = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.SPAIN === storeId
  }

  public static readonly isBrazil = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.BRAZIL === storeId
  }

  public static readonly isKorea = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.KOREA === storeId
  }

  public static readonly isAustralia = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { storeId } = singleton.wcsConfig
    return WcsStoreId.AUSTRALIA === storeId
  }

  public static readonly isStaticPage = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === STATIC_PAGE_VIEW || viewTaskName === COREMEDIA_CONTENT
  }

  public static readonly isInTYP = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === ORDER_OK_VIEW || viewTaskName === THANK_YOU_PAGE_VIEW
  }

  public static readonly isInPaymentPage = (): boolean => {
    const singleton = WcsConfigSingleton.getInstance()
    const { viewTaskName } = singleton.wcsConfig
    return viewTaskName === PAYMENT_DETAILS_VIEW || viewTaskName === PAYMENT_REDESIGN_DETAILS_VIEW
  }

  private normalizeWcsConfig = (config: any): WcsConfig =>
    ({
      countryPrefix: config.topCategoriesDisplayURL,
      searchProvider: config.searchProvider,
      algolia: {
        applicationId: config.algolia?.applicationId,
        apiKey: config.algolia?.apiKey,
        contentsIndex: config.algolia?.contentsIndex,
        groupedIndex: config.algolia?.groupedIndex,
        ungroupedIndex: config.algolia?.ungroupedIndex,
        YMALCarouselsEnabled: config.algolia?.YMALCarouselsEnabled === TRUE_LOWERCASE,
        accessoriesCarouselsEnabled: config.algolia?.accessoriesCarouselsEnabled === TRUE_LOWERCASE,
        minicartCarouselsEnabled: config.algolia?.minicartCarouselsEnabled === TRUE_LOWERCASE,
      },
      eyewearProtection: {
        isEnabled: config.eyewearProtection?.isEnabled === TRUE_LOWERCASE,
        quebec: {
          isCartNoticeEnabled:
            config.eyewearProtection?.quebec?.isCartNoticeEnabled === TRUE_LOWERCASE,
          isDeliveryEnabled: config.eyewearProtection?.quebec?.isDeliveryEnabled === TRUE_LOWERCASE,
          isPickupEnabled: config.eyewearProtection?.quebec?.isPickupEnabled === TRUE_LOWERCASE,
        },
      },
      payOverTime: {
        afterpay: {
          checkoutTermsConditionUrl: config.afterpayCheckoutTermsConditionUrl,
          enabled: config.afterpayEnabled === Y,
          installments: config.afterpayInstallments,
          thresholdMax: config.afterpayThresholdMax,
          thresholdMin: config.afterpayThresholdMin,
          localizedMessage: {
            de_CH: config.payOverTimeMessageAfterpay_de_CH,
            default: config.payOverTimeMessageAfterpay,
            en_CA: config.payOverTimeMessageAfterpay_en_CA,
            en_CH: config.payOverTimeMessageAfterpay_en_CH,
            en_HK: config.payOverTimeMessageAfterpay_en_HK,
            fr_BE: config.payOverTimeMessageAfterpay_fr_BE,
            fr_CA: config.payOverTimeMessageAfterpay_fr_CA,
            fr_CH: config.payOverTimeMessageAfterpay_fr_CH,
            it_CH: config.payOverTimeMessageAfterpay_it_CH,
            nl_BE: config.payOverTimeMessageAfterpay_nl_BE,
            zh_HK: config.payOverTimeMessageAfterpay_zh_HK,
          },
        },
        klarna: {
          locale: config.klarna_locale,
          installments: +config.klarnaInstallments,
          onSiteMessagingEnabled: config.klarnaOnSiteMessagingEnabled === TRUE_LOWERCASE,
          localizedMessage: {
            de_CH: config.payOverTimeMessageKlarna_de_CH,
            default: config.payOverTimeMessageKlarna,
            en_CA: config.payOverTimeMessageKlarna_en_CA,
            en_CH: config.payOverTimeMessageKlarna_en_CH,
            en_HK: config.payOverTimeMessageKlarna_en_HK,
            fr_BE: config.payOverTimeMessageKlarna_fr_BE,
            fr_CA: config.payOverTimeMessageKlarna_fr_CA,
            fr_CH: config.payOverTimeMessageKlarna_fr_CH,
            it_CH: config.payOverTimeMessageKlarna_it_CH,
            nl_BE: config.payOverTimeMessageKlarna_nl_BE,
            zh_HK: config.payOverTimeMessageKlarna_zh_HK,
          },
        },
        payPal: {
          enabled: config.paypalLaterEnabled === TRUE_LOWERCASE,
          localizedMessage: {
            de_CH: config.payOverTimeMessagePaypal_de_CH,
            default: config.payOverTimeMessagePayPal,
            en_CA: config.payOverTimeMessagePayPal_en_CA,
            en_CH: config.payOverTimeMessagePayPal_en_CH,
            en_HK: config.payOverTimeMessagePayPal_en_HK,
            fr_BE: config.payOverTimeMessagePayPal_fr_BE,
            fr_CA: config.payOverTimeMessagePayPal_fr_CA,
            fr_CH: config.payOverTimeMessagePayPal_fr_CH,
            it_CH: config.payOverTimeMessagePayPal_it_CH,
            nl_BE: config.payOverTimeMessagePayPal_nl_BE,
            zh_HK: config.payOverTimeMessagePayPal_zh_HK,
          },
        },
      },
      applePayMobStickyBarEnabled: config.applePayMobStickyBarEnabled === TRUE_LOWERCASE,
      brand: config.brand,
      browsers: config.browsers ?? [],
      browserVersions: config.browserVersions,
      catalogId: config.catalogId,
      cmsRegistrationFormConfiguration: config.cmsRegistrationFormConfiguration,
      currency: config.currency,
      currencySymbol: config.currencySymbol,
      doctorLocator: {
        script: config.doctorLocator?.script,
      },
      earlyAccess: {
        active: config.earlyAccessActive === TRUE_LOWERCASE,
        cookie: config.earlyAccessCookie,
        url: config.earlyAccessUrl,
        urlParamValue: config.earlyAccessUrlParamValue,
        user_cluster: config.user_cluster,
      },
      pagesWithStickyStoriesSlider: config.enableStoriesSliderScroll,
      pagesWithoutChat: config.pagesWithoutChat,
      pagesWithoutNewsletter: config.pagesWithoutNewsletter,
      tooltipPDPCategories: config.tooltipPDPCategories,
      facebookLogin: config.facebookLogin === TRUE_LOWERCASE,
      findStoreUrl: config.findStoreUrl,
      isAsianFitAvailable: config.isAsianFitAvailable === TRUE_LOWERCASE,
      isEmailAvailableWhenContactCenterIsOff:
        config.isEmailAvailableWhenContactCenterIsOff === TRUE_LOWERCASE,
      isInsuranceEnabled: config.isInsuranceEnabled === TRUE_LOWERCASE,
      isPickupEnabled: config.isPickupEnabled === TRUE_LOWERCASE,
      isPSD2_Enabled: config.isPSD2_Enabled === TRUE_LOWERCASE,
      isSearchClusterEnabled: config.isSearchClusterEnabled,
      isStellaPublished: config.isStellaPublished === TRUE_LOWERCASE,

      assetMiddleLayerImagesHost: config.assetMiddleLayerImagesHost ?? 'https://images.ray-ban.com',
      langId: config.langId,
      limitedQuantityThreshold: +config.limitedQuantityThreshold,
      locale: config.locale,
      mentionMe: {
        referrer: {
          src: config.mentionMe?.referrer?.src,
        },
        referee: {
          src: config.mentionMe?.referee?.src,
        },
      },
      minicartCrossSellingCarouselIsEnabled:
        config.minicartCrossSellingCarouselIsEnabled === TRUE_LOWERCASE,
      minicartPromoLabels: config.minicartPromoLabels,
      newsletterOverlay: {
        isEnabled: config.newsletterOverlay?.isEnabled?.toLowerCase() === TRUE_LOWERCASE,
        isEnabledForDesktop: config.newsletterOverlay?.isEnabledForDesktop === Y,
        isEnabledForMobile: config.newsletterOverlay?.isEnabledForMobile === Y,
      },
      newsletterOverlayType: config.newsletterOverlayType,
      openMenu: config.openMenu === TRUE_LOWERCASE,
      orderId: config.orderId,
      pageName: config.pageName,
      pdp: {
        isStickyBarAddPrescriptionLensesButtonEnabled:
          config.pdp?.isStickyBarAddPrescriptionLensesButtonEnabled === TRUE_LOWERCASE,
        isRoxPromoMessageEnabledForStella: config.pdp?.isRoxPromoMessageEnabledForStella,
        estimatedDeliveryDateIsVisible:
          config.pdp?.estimatedDeliveryDateIsVisible === TRUE_LOWERCASE,
        earlyAccessStatusMessageLabel: config.pdp?.earlyAccessStatusMessageLabel,
        prescriptionAvailableDisclaimerIsVisible:
          config.pdp?.prescriptionAvailableDisclaimerIsVisible === TRUE_LOWERCASE,
        findInStoreIsEnabled: config.isCnCEnabled === TRUE_LOWERCASE,
        polarLensUpsellEnabled: config.pdpPolarUpsell === TRUE_LOWERCASE,
        customizeItPromoMessageSun: {
          de_CH: config.customizeItPromoMessageSun_de_CH,
          en_CA: config.customizeItPromoMessageSun_en_CA,
          en_CH: config.customizeItPromoMessageSun_en_CH,
          en_HK: config.customizeItPromoMessageSun_en_HK,
          fr_BE: config.customizeItPromoMessageSun_fr_BE,
          fr_CA: config.customizeItPromoMessageSun_fr_CA,
          fr_CH: config.customizeItPromoMessageSun_fr_CH,
          it_CH: config.customizeItPromoMessageSun_it_CH,
          nl_BE: config.customizeItPromoMessageSun_nl_BE,
          zh_HK: config.customizeItPromoMessageSun_zh_HK,
          default: config.customizeItPromoMessageSun,
        },
        customizeItPromoMessageEye: {
          de_CH: config.customizeItPromoMessageEye_de_CH,
          en_CA: config.customizeItPromoMessageEye_en_CA,
          en_CH: config.customizeItPromoMessageEye_en_CH,
          en_HK: config.customizeItPromoMessageEye_en_HK,
          fr_BE: config.customizeItPromoMessageEye_fr_BE,
          fr_CA: config.customizeItPromoMessageEye_fr_CA,
          fr_CH: config.customizeItPromoMessageEye_fr_CH,
          it_CH: config.customizeItPromoMessageEye_it_CH,
          nl_BE: config.customizeItPromoMessageEye_nl_BE,
          zh_HK: config.customizeItPromoMessageEye_zh_HK,
          default: config.customizeItPromoMessageEye,
        },
        roxPromoMessageSun: {
          de_CH: config.roxPromoMessageSun_de_CH,
          en_CA: config.roxPromoMessageSun_en_CA,
          en_CH: config.roxPromoMessageSun_en_CH,
          en_HK: config.roxPromoMessageSun_en_HK,
          fr_BE: config.roxPromoMessageSun_fr_BE,
          fr_CA: config.roxPromoMessageSun_fr_CA,
          fr_CH: config.roxPromoMessageSun_fr_CH,
          it_CH: config.roxPromoMessageSun_it_CH,
          nl_BE: config.roxPromoMessageSun_nl_BE,
          zh_HK: config.roxPromoMessageSun_zh_HK,
          default: config.roxPromoMessageSun,
        },
        roxPromoMessageEye: {
          de_CH: config.roxPromoMessageEye_de_CH,
          en_CA: config.roxPromoMessageEye_en_CA,
          en_CH: config.roxPromoMessageEye_en_CH,
          en_HK: config.roxPromoMessageEye_en_HK,
          fr_BE: config.roxPromoMessageEye_fr_BE,
          fr_CA: config.roxPromoMessageEye_fr_CA,
          fr_CH: config.roxPromoMessageEye_fr_CH,
          it_CH: config.roxPromoMessageEye_it_CH,
          nl_BE: config.roxPromoMessageEye_nl_BE,
          zh_HK: config.roxPromoMessageEye_zh_HK,
          default: config.roxPromoMessageEye,
        },
        roxPromoMessageElectronics: {
          de_CH: config.roxPromoMessageElectronics_de_CH,
          en_CA: config.roxPromoMessageElectronics_en_CA,
          en_CH: config.roxPromoMessageElectronics_en_CH,
          en_HK: config.roxPromoMessageElectronics_en_HK,
          fr_BE: config.roxPromoMessageElectronics_fr_BE,
          fr_CA: config.roxPromoMessageElectronics_fr_CA,
          fr_CH: config.roxPromoMessageElectronics_fr_CH,
          it_CH: config.roxPromoMessageElectronics_it_CH,
          nl_BE: config.roxPromoMessageElectronics_nl_BE,
          zh_HK: config.roxPromoMessageElectronics_zh_HK,
          default: config.roxPromoMessageElectronics,
        },
        orderOfAccordionsSun: config.pdp?.orderOfAccordionsSun
          ? config.pdp?.orderOfAccordionsSun?.split(',')
          : [],
        orderOfAccordionsEye: config.pdp?.orderOfAccordionsEye
          ? config.pdp?.orderOfAccordionsEye?.split(',')
          : [],
        orderOfAccordionsElectronics: config.pdp?.orderOfAccordionsElectronics
          ? config.pdp?.orderOfAccordionsElectronics?.split(',')
          : [],
        isOpticsSellable: config.pdp?.isOpticsSellable === Y,
        dynamicStatusMessage: {
          content: config.pdp?.dynamicStatusMessageContent,
          promoCategories: config.pdp?.dynamicStatusMessagePromoCategories?.split(',') ?? [],
        },
        statusMessageLabelColour: config.pdp?.statusMessageLabelColour ?? 'red',
        imagesPath: config.imagesPdpPath,
      },
      plp: {
        mobileDefaultView: config.plp_mobile_default_view,
        areFiltersCompressed: config.isPlpFiltersCompressed === TRUE_LOWERCASE,
        areFiltersOpen: config.isPlpFiltersOpen === TRUE_LOWERCASE,
        priceFilter: {
          step: +config.plp?.priceFilter?.step ?? 1,
          distance: +config.plp?.priceFilter?.distance ?? 1,
        },
        priceFormat: config.plp?.priceFormat ?? `${CURRENCY_PLACEHOLDER} ${PRICE_PLACEHOLDER}`,
        categoryId: config.plp?.categoryId,
        promoTogglesAreVisible: config.plp?.promoTogglesAreVisible,
        facetsWithExclusiveBehaviour: config.plp?.facetsWithExclusiveBehaviour?.split(',') ?? [],
        explodedCategories: config.plp?.explodedCategories?.split(',') ?? [],
      },
      productDetailsTitle: config.productDetailsTitle,
      pushNotification: {
        clicksNumberToOpening: isNaN(+config.pushNotification?.clicksNumberToOpening)
          ? 0
          : +config.pushNotification?.clicksNumberToOpening,
      },
      flashSale: {
        redCountdownStart: config.redCountdownStart,
        redIconStart: config.redIconStart,
      },
      header: {
        omnichannel: {
          supportServices: config.header?.omnichannel?.supportServices,
          inStoreServices: config.header?.omnichannel?.inStoreServices,
          deliveryServices: config.header?.omnichannel?.deliveryServices,
          discoverAllServices: config.header?.omnichannel.discoverAllServices,
        },
        search: {
          noResults: {
            facetName: config.header?.search?.noResults?.facetName,
            facetValue: config.header?.search?.noResults?.facetValue,
          },
        },
      },
      minicartPromoStrip: {
        de_CH: config.minicartPromoStrip?.de_CH,
        en_CA: config.minicartPromoStrip?.en_CA,
        en_CH: config.minicartPromoStrip?.en_CH,
        en_HK: config.minicartPromoStrip?.en_HK,
        fr_BE: config.minicartPromoStrip?.fr_BE,
        fr_CA: config.minicartPromoStrip?.fr_CA,
        fr_CH: config.minicartPromoStrip?.fr_CH,
        it_CH: config.minicartPromoStrip?.it_CH,
        nl_BE: config.minicartPromoStrip?.nl_BE,
        zh_HK: config.minicartPromoStrip?.zh_HK,
        default: config.minicartPromoStrip?.default,
      },
      scrollToStellaSection: config.scrollToStellaSection === TRUE_LOWERCASE,
      searchByImageIntegrationKey: config.searchByImageIntegrationKey,
      skipSelfAndMyAccountForStella: config.skipSelfAndMyAccountForStella === TRUE_LOWERCASE,
      storeId: config.storeId,
      storeLocator: {
        description: config.storeLocatorDescription,
        stellaCountries: config.storeLocatorStellaCountries,
        zoomLevel: config.storeLocatorZoomLevel ?? 15,
        isStoreLocatorLabelVisible: config.isStoreLocatorLabelVisible === TRUE_LOWERCASE,
        countriesWithBookAnAppointment: config.countriesWithBookAnAppointment,
        countriesWithLensSwap: config.countriesWithLensSwap,
      },
      shipToStore: {
        maxItems: config.sts_maxItems,
        radius: config.sts_radius,
      },
      trustPilot: {
        businessId: config.trustPilot?.businessId,
        footer: {
          enabled: config.trustPilot?.footer.enabled === TRUE_LOWERCASE,
          target: config.trustPilot?.footer.target,
          templateId: config.trustPilot?.footer.templateId,
        },
        productCarousel: {
          enabled: config.trustPilot?.productCarousel.enabled === TRUE_LOWERCASE,
          target: config.trustPilot?.productCarousel.target,
          templateId: config.trustPilot?.productCarousel.templateId,
        },
        productMini: {
          enabled: config.trustPilot?.productMini.enabled === TRUE_LOWERCASE,
          target: config.trustPilot?.productMini.target,
          templateId: config.trustPilot?.productMini.templateId,
        },
        typ: {
          enabled: config.trustPilot?.typ.enabled === TRUE_LOWERCASE,
          target: config.trustPilot?.typ.target,
          templateId: config.trustPilot?.typ.templateId,
        },
        scriptUrl: config.trustPilot?.scriptUrl,
      },
      genesys: {
        deploymentId: config.genesys?.deploymentId,
        environment: config.genesys?.environment,
        scriptUrl: config.genesys?.scriptUrl,
      },
      userId: config.userId,
      viewTaskName: config.viewTaskName,
      virtualMirror: {
        active: config.virtualMirror?.bipaActive === TRUE_LOWERCASE,
        key: config.virtualMirror?.bipaKey,
        region: config.virtualMirror?.bipaRegion,
        source: config.virtualMirror?.bipaSource,
        script: {
          src: config.virtualMirror?.script,
        },
        isPictureModeEnabled: config?.virtualMirror?.isPictureModeEnabled === TRUE_LOWERCASE,
        isUploadPictureEnabled: config?.virtualMirror?.isUploadPictureEnabled === TRUE_LOWERCASE,
        isTakePictureEnabled: config?.virtualMirror?.isTakePictureEnabled === TRUE_LOWERCASE,
        isTryOnEnabled: config?.virtualMirror?.isTryOnEnabled === TRUE_LOWERCASE,
        isVideoModeEnabled: config?.virtualMirror?.isVideoModeEnabled === TRUE_LOWERCASE,
        isQrCodeEnabled: config?.virtualMirror?.isQrCodeEnabled === TRUE_LOWERCASE,
        isTakeScreenshotEnabled: config?.virtualMirror?.isTakeScreenshotEnabled === TRUE_LOWERCASE,
        disableSavePolaroid: config?.virtualMirror?.disableSavePolaroid === TRUE_LOWERCASE,
        categoriesWithoutAddToBag:
          config?.virtualMirror?.categoriesWithoutAddToBag?.split(',') ?? [],
      },
      frameGenius: {
        faCore: {
          src: config.frameGenius?.faCore,
        },
        pageSize: +config.frameGenius?.pageSize,
        isSizeAdvisorEnabled: config.frameGenius?.enableSizeAdvisor === TRUE_LOWERCASE,
        isFloatingButtonFeatureEnabled:
          config.frameGenius?.isFloatingButtonFeatureEnabled === TRUE_LOWERCASE,
        isVideoModeEnabled: config.frameGenius?.isVideoModeEnabled === TRUE_LOWERCASE,
        routesWithoutSizeAdvisor: config.frameGenius?.routesWithoutSizeAdvisor,
      },
      manualInput: {
        maxUploadSizeInBytes: config.manualInput?.maxUploadSizeInBytes,
        supportedFileExtension: config.manualInput?.supportedFileExtension,
      },
      roxLensPanel: {
        isMixAndMatchButtonLabelVisible:
          config.roxLensPanel?.isMixAndMatchButtonLabelVisible === TRUE_LOWERCASE,
        isPrescriptionModuleEnabled:
          config.roxLensPanel?.isPrescriptionModuleEnabled === TRUE_LOWERCASE,
        style: {
          href: config.roxLensPanel?.style,
        },
        rxc: {
          src: config.roxLensPanel?.rxc,
        },
        thousandSeparator: config.thousandSeparatorRoxConfig,
        decimalSeparator: config.decimalSeparatorRoxConfig,
        isEyemedEnabled: config.isEyemedEnabledInRoxPanel === TRUE_LOWERCASE,
        isEnabled: config.isRoxPanelEnabled === TRUE_LOWERCASE,
        autoDiscountBadge: config.roxLensPanel?.autoDiscountBadge,
        translations: config.roxLensPanel?.translations,
        enablePrism: config.roxLensPanel?.enablePrism === TRUE_LOWERCASE,
        isVMMVEnabledInRoxPanelForClear:
          config?.roxLensPanel?.isVMMVEnabledInRoxPanelForClear === TRUE_LOWERCASE,
        isVMMVEnabledInRoxPanelForSun:
          config?.roxLensPanel?.isVMMVEnabledInRoxPanelForElectronics === TRUE_LOWERCASE,
        isVMMVEnabledInRoxPanelForElectronics:
          config?.roxLensPanel?.isVMMVEnabledInRoxPanelForElectronics === TRUE_LOWERCASE,
        flowNameSunglasses: config?.roxLensPanel?.flowNameSunglasses ?? '',
        flowNameEyeglasses: config?.roxLensPanel?.flowNameEyeglasses ?? '',
        flowNameElectronics: config?.roxLensPanel?.flowNameElectronics ?? '',
      },
      shipFromStore: {
        minimumOrderPrice: config.sfsMinimumOrderPrice,
        itemsInCartCount: config.sfsItemsInCartCount,
      },
      isUnavailableSizeModalEnabled: config.isUnavailableSizeModalEnabled,
      isGreenShippingInFirstPosition: config.isGreenShippingInFirstPosition,
      cancelOrderFaqId: config.cancelOrderFaqId ?? '',
      insuranceDisclaimerEnabled: config.insuranceDisclaimerEnabled === TRUE_LOWERCASE,
      googleMaps: {
        src: config.googleMapsApiUrl,
      },
      checkPrescriptionIssueDate: config.checkPrescriptionIssueDate ?? 0,
      applepayEnabledForPreorder: config.applepayEnabledForPreorder === TRUE_LOWERCASE,
      klarnaExpress: {
        clientKey: config.klarnaExpress?.clientKey,
        scriptUrl: config.klarnaExpress?.scriptUrl,
        environment: config.klarnaExpress?.environment,
        merchantId: config.klarnaExpress?.merchantId,
      },
      categoriesToExcludeForTrendingFacets: config.categoriesToExcludeForTrendingFacets,
      transitionBannerUrl: config.transitionBannerUrl,
      enablePromoCopyRBM: config.enablePromoCopyRBM === TRUE_LOWERCASE,
      isSizeMVPEnabled: config.isSizeMVPEnabled === TRUE_LOWERCASE,
      stickyStripeEnabled: config.StickyStripeEnabled === TRUE_LOWERCASE,
      stickyHeaderEnabled: config.StickyHeaderEnabled === TRUE_LOWERCASE,
      showDoctorName: config.checkPrescriptionDoctorName === TRUE_LOWERCASE,
      manualPrescriptionIsEnabled: config.manualPrescriptionEnabled === TRUE_LOWERCASE,
      uploadPrescriptionIsEnabled: config.uploadPrescriptionEnabled === TRUE_LOWERCASE,
      sendItLaterPrescriptionIsEnabled: config.sendItLaterPrescriptionEnabled === TRUE_LOWERCASE,
    } as WcsConfig)
}
